/**
 * A scheduled call, in words.
 *
 * Shared by every screen that shows one, because "in 20 minutes", "today
 * at 12:00" and "overdue by an hour" have to mean the same thing on the
 * candidate page, in the to-do list and in the table — and because the
 * ordering rule below is the whole feature: a call is the only thing in
 * this pipeline with a time on it, so on the day it falls it outranks
 * everything else.
 */

const MINUTE = 60 * 1000;

export type CallUrgency = 'overdue' | 'now' | 'today' | 'soon' | 'later' | 'none';

export interface CallState {
  urgency: CallUrgency;
  /** "in 20 minutes", "overdue by 2 hours", "Tue at 12:00". */
  words: string;
  /** For sorting: overdue first, then soonest. */
  rank: number;
  at: Date | null;
}

const clock = (date: Date) => date.toLocaleTimeString('en-US', {
  hour: 'numeric', minute: '2-digit',
});

const day = (date: Date) => date.toLocaleDateString('en-US', {
  weekday: 'short', month: 'short', day: 'numeric',
});

export function callState(
  scheduledCallAt?: string | null,
  now: Date = new Date(),
  /** The interview already happened — the time on the record is history. */
  held = false,
): CallState {
  // A call that has BEEN taken is not late, however long ago it was
  // booked for. Ticking "Interview completed" now takes the time off the
  // record, so this is for the rows written before it did — and for the
  // half-second between the tick and the reload.
  if (held || !scheduledCallAt) {
    return { urgency: 'none', words: '', rank: Number.MAX_SAFE_INTEGER, at: null };
  }

  const at = new Date(scheduledCallAt);
  if (Number.isNaN(at.getTime())) {
    return { urgency: 'none', words: '', rank: Number.MAX_SAFE_INTEGER, at: null };
  }

  const minutes = Math.round((at.getTime() - now.getTime()) / MINUTE);
  const sameDay = at.toDateString() === now.toDateString();

  // Overdue sorts ABOVE everything, including a call five minutes from
  // now: a call nobody made is the clearest overdue job there is, and it
  // stays at the top until somebody deals with it.
  if (minutes < -2) {
    const late = -minutes < 60
      ? `${-minutes} min ago`
      : `${Math.round(-minutes / 60)}h ago`;
    return { urgency: 'overdue', words: `Missed · ${clock(at)}, ${late}`, rank: minutes, at };
  }

  if (minutes <= 5) return { urgency: 'now', words: `Call now · ${clock(at)}`, rank: 1_000 + minutes, at };
  if (minutes <= 60) return { urgency: 'soon', words: `In ${minutes} min · ${clock(at)}`, rank: 1_000 + minutes, at };
  if (sameDay) return { urgency: 'today', words: `Today ${clock(at)}`, rank: 1_000 + minutes, at };
  return { urgency: 'later', words: `${day(at)}, ${clock(at)}`, rank: 1_000 + minutes, at };
}

/** Does this one belong at the top of the to-do list today? */
export const isPressing = (state: CallState) => (
  state.urgency === 'overdue' || state.urgency === 'now'
  || state.urgency === 'soon' || state.urgency === 'today'
);

/** A datetime-local input value from an ISO string, and back. */
export const toInput = (iso?: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  // datetime-local wants LOCAL wall-clock time with no zone, so the ISO
  // string cannot be sliced — that would show a UTC time in the box and
  // move the call by however many hours the office is from Greenwich.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const fromInput = (value: string): string | null => (
  value ? new Date(value).toISOString() : null
);

/** Midday, because that is the hour people say when they say a day. */
export const DEFAULT_HOUR = 12;

/**
 * The answers people actually give: a DAY. The hour is midday unless
 * somebody says otherwise, which is what the date-and-time box is for.
 */
export function quickTimes(now: Date = new Date()): Array<{ label: string; value: string }> {
  const at = (days: number, hour = DEFAULT_HOUR) => {
    const date = new Date(now);
    date.setDate(date.getDate() + days);
    date.setHours(hour, 0, 0, 0);
    return date;
  };

  // The Monday coming. On a Monday that means next week's, not today —
  // somebody saying "next Monday" on a Monday does not mean in an hour.
  const daysToMonday = ((8 - now.getDay()) % 7) || 7;

  const options = [
    { label: 'Today', value: toInput(at(0).toISOString()) },
    { label: 'Tomorrow', value: toInput(at(1).toISOString()) },
    { label: 'Next Monday', value: toInput(at(daysToMonday).toISOString()) },
  ];

  // Midday has gone; offering it as "Today" would book a call in the past.
  return now.getHours() >= DEFAULT_HOUR ? options.slice(1) : options;
}
